import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import CreditsBadge from '../CreditsBadge/CreditsBadge';
import logo from '../../ugotai_Logo_n.svg';
import { UserCircle2, Settings, LogOut,  Eye, EyeOff } from 'lucide-react';

    



const Navbar = () => {
  // FIX: Added 'user' to display the email when logged in
  const { isAuthenticated, user, logout, credits } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  // FIX: Changed 'key' to match the keys in your translation JSON files
  const navItems = [
    { key: 'Text tools', path: '/text-tools' },
    { key: 'Image tools', path: '/image-tools' },
    { key: 'Video tools', path: '/video-tools' },
    { key: 'Audio tools', path: '/audio-tools' },
    { key: 'Services', path: '/services' },
    { key: 'LitBot', path: '/litbot' },
//    { key: 'Tutorials', path: '/tutorials' },
    { key: 'Pricing', path: '/pricing' },
    { key: 'Contact', path: '/contact' },


  ];

  const [showCredits, setShowCredits] = useState(() => {
    const saved = localStorage.getItem('showCredits');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('showCredits', JSON.stringify(showCredits));
  }, [showCredits]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false); // Close menu on logout
  };

  const handleLinkClick = (path) => {
    navigate(path);
    setIsMenuOpen(false); // Close menu on navigation
  };

  useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setIsProfileMenuOpen(false);
            }
        };
        // Add event listener when the menu is open
        if (isProfileMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        // Cleanup the event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isProfileMenuOpen]);

  return (
    <nav className="sticky top-0 z-50 bg-brand-dark/75 backdrop-blur-md border-b border-slate-700">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center space-x-2">
            <img src={logo} alt="Logo" className="h-12 w-auto" />
          </Link>

          {/* Desktop Navigation Links (Center) */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className="text-gray-300 hover:text-orange-400 px-3 py-2 text-sm font-medium transition-colors"
              >
                {t(item.key)}
              </Link>
            ))}
          </div>

          {/* Right side of Navbar (Desktop) */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="w-28">
              <LanguageSelector />
            </div>
            {isAuthenticated ? (
              <>
                <div className="flex items-center gap-2">
                  <Link to={user?.subscription_status === 'active' ? "/purchase-credits" : "/pricing"}>
                    <button className="bg-orange-500 hover:bg-brand text-white px-3 py-1.5 text-sm font-semibold transition-colors">
                      + credits: {showCredits ? credits : '***'}
                    </button>
                  </Link>
                  <button
                    onClick={() => setShowCredits(!showCredits)}
                    className="p-2 text-slate-400 hover:text-slate-300 transition-colors"
                    title={showCredits ? t('Hide credits') : t('Show credits')}
                  >
                    {showCredits ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>


                <div className="relative" ref={profileMenuRef}>
                    <button 
                        onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                        className="flex items-center justify-center h-10 w-10 rounded-full bg-slate-700 hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-dark focus:ring-brand-orange"
                    >
                        {/* We can eventually add the user's profile picture here */}
                        <UserCircle2 className="h-6 w-6 text-slate-300" />
                    </button>

                    {isProfileMenuOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 shadow-lg py-1">
                            <Link
                                to="/account"
                                onClick={() => setIsProfileMenuOpen(false)}
                                className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 w-full text-left"
                            >
                                <Settings className="mr-2 h-4 w-4" />
                                {t('account.title')}
                            </Link>
                            <button
                                onClick={handleLogout}
                                className="flex items-center px-4 py-2 text-sm text-red-400 hover:bg-slate-700 w-full text-left"
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                {t('Logout')}
                            </button>
                        </div>
                    )}
                </div>



              </>
            ) : (
              <>
                <Link to="/login" className="text-gray-300 hover:text-orange-400 font-medium text-sm">
                  {t('Login')}
                </Link>
                <Link to="/signup" className="bg-brand-orange hover:bg-orange-600 text-white px-3 py-1.5 text-sm font-semibold transition-colors">
                  {t('Signup')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m4 6h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* --- UNIFIED MOBILE & SIDEBAR MENU --- */}
      {isMenuOpen && (
        <div className="md:hidden bg-slate-800 absolute w-full left-0 border-t border-slate-700 p-4 space-y-3">
          {/* Main Links */}
          {navItems.map((item) => (
            <button key={item.key} onClick={() => handleLinkClick(item.path)} className="w-full text-left block text-gray-300 hover:text-orange-400 py-2 font-medium">
              {t(item.key)}
            </button>
          ))}
          
          <div className="border-t border-slate-700 !my-4"></div>

          {/* Language Selector for Mobile */}
          <div>
            <LanguageSelector />
          </div>

          {/* Auth Links for Mobile */}
          {isAuthenticated && (
            <div className="flex items-center justify-between bg-slate-700 p-3 rounded">
              <Link to={user?.subscription_status === 'active' ? "/purchase-credits" : "/pricing"} className="flex-1">
                <span className="text-orange-400 font-semibold">
                  + credits: {showCredits ? credits : '***'}
                </span>
              </Link>
              <button
                onClick={() => setShowCredits(!showCredits)}
                className="p-1 text-slate-400 hover:text-slate-300 transition-colors ml-2"
              >
                {showCredits ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            )}
          {isAuthenticated ? (
            <>
               <button onClick={() => handleLinkClick('/account')} className="w-full text-left block text-gray-300 hover:text-orange-400 py-2 font-medium">
                {t('account.title')}
              </button>
              <button onClick={handleLogout} className="w-full text-left block text-red-400 hover:text-red-500 py-2 font-medium">
                {t('logout')}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => handleLinkClick('/login')} className="w-full text-left block text-gray-300 hover:text-orange-400 py-2 font-medium">
                {t('Login')}
              </button>
              <button onClick={() => handleLinkClick('/signup')} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 font-semibold transition-colors">
                {t('Signup')}
              </button>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
